// Lightweight Web Notifications wrapper. iOS Safari support is limited;
// we fail silently where the API doesn't exist.

const PERM_KEY = 'action_notif_permission'

export function notificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window
}

export function getStoredPermission() {
  try { return localStorage.getItem(PERM_KEY) } catch { return null }
}

function storePermission(state) {
  try { localStorage.setItem(PERM_KEY, state) } catch {}
}

/**
 * Request notification permission. Safe to call multiple times — if the user
 * has already decided, returns the cached decision without re-prompting.
 * Returns the final permission state: 'granted' | 'denied' | 'default' | 'unsupported'.
 */
export async function requestNotificationPermission() {
  if (!notificationsSupported()) return 'unsupported'
  const current = Notification.permission
  if (current === 'granted' || current === 'denied') {
    storePermission(current)
    return current
  }
  try {
    const result = await Notification.requestPermission()
    storePermission(result)
    return result
  } catch {
    return 'default'
  }
}

/**
 * Fire a notification only if the tab is hidden/unfocused — no need to
 * disturb a player who's already watching the screen.
 */
export function notifyIfBackground(title, body, { tag } = {}) {
  if (!notificationsSupported()) return
  if (Notification.permission !== 'granted') return
  // Skip if tab is visible AND focused — user is already looking at the app
  if (document.visibilityState === 'visible' && document.hasFocus()) return
  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.png',
      tag: tag || 'action-party',
      renotify: !!tag,
    })
    // Focus the tab when the user clicks the notification
    n.onclick = () => {
      window.focus()
      n.close()
    }
  } catch {
    // Fail silently — older browsers, denied permissions, etc.
  }
}
